import { PERSON_SCOPED_ROLES, type Role } from './enums';
import { ADMIN_PAGE_PERMISSIONS } from './permission-catalog';
import type { Permission } from './permissions';

/**
 * Разделы портала: что бывает пунктом меню, маршрутом и стартовой страницей.
 *
 * Один список, потому что спрашивающих трое — меню (`AppLayout`), маршруты с их гейтами (`App.tsx`)
 * и стартовая страница (`HomeRedirect`), — и до этого файла каждый знал свой состав разделов
 * (`docs/portal-sections-plan.md` §2). Новый раздел заводили в двух копиях из трёх, и промах
 * выходил наружу только у той роли, для которой пропущенный раздел — единственный: так стартовая
 * страница потеряла «Орг.технику» (ADR 0085) и отправляла оператора сервисной компании на смену
 * пароля. Та же болезнь и тем же лечением уже правилась в `ADMIN_PAGE_PERMISSIONS`
 * (`docs/manuals-plan.md` §3.6).
 *
 * Здесь, а не во фронте, по причине из `links.ts`: адреса портала уже живут в контрактах, и ответ
 * на «какие у портала разделы» обязан быть один. Плюс тест полноты оказывается рядом с
 * `ACCESS_PROFILES` — там, где перебирают всех, кто в портале бывает.
 *
 * React сюда не приходит: иконки и бейджи остаются в каркасе (§4.2 плана). Реестр — данные, и
 * знать про способ отрисовки ему нечем.
 *
 * Что реестром **не** описывается: вкладки внутри разделов, подстраницы со своим правом
 * (`/vehicle-requests/weekly/:id`), публичные маршруты и служебная смена пароля. Раздел — это то,
 * что бывает пунктом меню и стартовой страницей; остальное закрывает `RequirePermission`.
 */

/** Разделы общего каркаса: пункт меню, маршрут в ветке `AppLayout`, страница в `SECTION_PAGES`. */
export type PortalShellSectionId =
  | 'waste'
  | 'vehicle-requests'
  | 'mechanization'
  | 'waybills'
  | 'garage'
  | 'office-equipment'
  | 'directories'
  | 'admin';

export type PortalSectionId = PortalShellSectionId | 'driver-cabinet';

interface SectionBase {
  /** Адрес раздела — он же ключ пункта меню и подсветки. */
  path: string;
  label: string;
  /** Подпись нижней навигации: на 360 px пункту достаётся четверть экрана (ADR 0030). */
  short: string;
  /** Раздел открывает ЛЮБОЕ из этих прав. */
  permissions: readonly Permission[];
  /** Дополнительное условие роли; сегодня только у кабинета водителя (ADR 0102). */
  roles?: readonly Role[];
  /** Каким по счёту раздел становится стартовым, если открыт учётке. */
  startOrder: number;
}

/**
 * Раздел каркаса. Отдельным типом от кабинета, а не полем-признаком с фильтром: `SECTION_PAGES` —
 * `Record` по `PortalShellSectionId`, и после `PORTAL_SECTIONS.filter(...)` обращение
 * `SECTION_PAGES[s.id]` перестало бы типизироваться — сужение пришлось бы держать предикатом,
 * то есть руками.
 */
export interface PortalShellSection extends SectionBase {
  id: PortalShellSectionId;
  shell: 'portal';
}

/** Второй контур (ADR 0102): свой каркас, своя ветка маршрутов, в меню не показывается. */
export interface DriverCabinetSection extends SectionBase {
  id: 'driver-cabinet';
  shell: 'own';
}

export type PortalSection = PortalShellSection | DriverCabinetSection;

/**
 * Разделы каркаса **в порядке меню** — том же, что стоял в `AppLayout` до реестра. Порядок здесь
 * продуктовый и объяснимый глазами: сначала работа с заявками, потом то, что из них собирается
 * (листы, гараж), потом справочное и служебное.
 *
 * Со стартовым порядком (`startOrder`) он намеренно не совпадает: это разные вопросы — «в каком
 * порядке человек видит разделы» и «какой из них ему открыть первым». Слей их в одно число, и
 * механику с назначенным `officeEquipment.read` (ADR 0106) стартовой страницей досталась бы
 * оргтехника вместо «Гаража».
 */
export const SHELL_SECTIONS: readonly PortalShellSection[] = [
  {
    id: 'waste',
    shell: 'portal',
    path: '/waste',
    label: 'Вывоз мусора',
    short: 'Вывоз',
    // Руководитель строительства отвечает за технику на объекте, вывоз мусора ведёт штаб (ADR 0025).
    // Область здесь значима: у отдела без площадки право есть, а работать им не над чем (ADR 0062),
    // и раздел закрывает `canUse` — единственное место реестра, где область вообще что-то решает.
    permissions: ['wasteRequests.read'],
    startOrder: 2,
  },
  {
    id: 'vehicle-requests',
    shell: 'portal',
    path: '/vehicle-requests',
    label: 'Заказ ТС',
    short: 'Заказ ТС',
    // Оператор вывоза — внешний перевозчик: заказ ТС к его работе отношения не имеет (ADR 0010).
    permissions: ['vehicleRequests.read'],
    // After the garage since ADR 0241: the mechanics read requests now, and their first page
    // stays the fleet on a date (see the garage below).
    startOrder: 4,
  },
  {
    id: 'mechanization',
    shell: 'portal',
    path: '/mech',
    label: 'Механизация',
    // «Мех.» — четыре знака: на 360 px пункту нижней навигации достаётся четверть экрана
    // (ADR 0030), а «Механизация» там не помещается ни в одну строку.
    short: 'Мех.',
    /*
     * Аренда малой механизации (план `docs/mechanization-module-plan.md`, Р16). Право одно —
     * чтение модуля, и область здесь значима ровно так же, как у вывоза мусора: отдел без
     * площадок раздела не видит вовсе (Р10, `MODULE_SCOPE`), потому что арендовать ему некуда.
     *
     * В меню — после «Заказа ТС»: оба отвечают на «чем работаем на площадке», и стоять они должны
     * рядом. Стартовым номером раздел уходит **в хвост** (`startOrder`) — по прецеденту
     * «Орг.техники» (ADR 0121): порядок меню и порядок стартовой страницы отвечают на разные
     * вопросы, и поставь мы механизацию третьей и там, площадка с открытыми обоими модулями
     * приземлялась бы не туда, куда приземляется сегодня.
     */
    permissions: ['mechRequests.read'],
    startOrder: 9,
  },
  {
    id: 'waybills',
    shell: 'portal',
    path: '/waybills',
    label: 'Путевые листы',
    short: 'Листы',
    // Журнал путевых листов (ADR 0037): его ведут те же, кто выписывает листы переводом заявок в
    // работу. В листе персональные данные водителя — потому и право своё.
    permissions: ['waybills.read'],
    startOrder: 5,
  },
  {
    id: 'garage',
    shell: 'portal',
    path: '/garage',
    label: 'Гараж',
    short: 'Гараж',
    // The garage (ADR 0076) is the day's snapshot: what own vehicles and drivers are busy with. In
    // the menu it follows the waybills it is built from, but as a start page it comes before them
    // and before «Заказ ТС»: the chief mechanic's service runs no requests, and the fleet on a date
    // is its first page. Since ADR 0241 the service reads requests too, so the garage must precede
    // them or mechanics would land in a list they only look at. Every role with both sections opens
    // «Вывоз мусора» first, so the order between these two moves nobody else.
    permissions: ['garage.read'],
    startOrder: 3,
  },
  {
    id: 'office-equipment',
    shell: 'portal',
    path: '/office-equipment',
    label: 'Орг.техника',
    short: 'Оргтех.',
    // Орг.техника (ADR 0085) — третий модуль заявок. Раздел открывают два права: заявки на
    // обслуживание — `serviceRequests.read`, парк техники — `officeEquipment.read` (Р72). Второе
    // есть у диспетчера, у которого модуля заявок нет вовсе, и по одному первому праву вкладка
    // «Техника» осталась бы для него за закрытой дверью. Менеджер стоял здесь рядом с ним до
    // 10.09.2026 — круг заявителя выдали ему ролью (ADR 0181), и теперь раздел открывают оба его
    // права сразу.
    permissions: ['serviceRequests.read', 'officeEquipment.read'],
    startOrder: 8,
  },
  {
    id: 'directories',
    shell: 'portal',
    path: '/directories',
    label: 'Справочники',
    short: 'Справ.',
    // Раздел открывают четыре права (ADR 0085, Р7): весь набор справочников ведёт `directories.write`,
    // одну вкладку «Оргтехника» — `officeEquipment.write`. Второе право заведено как раз затем,
    // чтобы ответственному за оргтехнику не выдавать первое вместе с объектами, контрагентами и
    // прайсом вывоза.
    //
    // Третье и четвёртое — права номенклатуры расходников: ими ведут картриджи и правят остаток, а
    // живёт этот справочник окном из той же вкладки «Оргтехника». Без них человек, которому выдали
    // ровно ведение номенклатуры, не увидел бы раздел в меню, а прямой адрес увёл бы его на
    // стартовую — то есть право было бы выдано, а двери к нему не было. Ведение парка от этого не
    // открывается: заведение и правка карточек техники по-прежнему спрашивают `officeEquipment.write`
    // сами.
    permissions: [
      'directories.write',
      'officeEquipment.write',
      'officeEquipmentConsumables.manage',
      'officeEquipmentConsumables.stock',
    ],
    startOrder: 6,
  },
  {
    id: 'admin',
    shell: 'portal',
    path: '/admin',
    label: 'Администрирование',
    short: 'Админ.',
    // Страница из вкладок под разными правами: учётки ведёт один человек, рассылки настраивает
    // другой, руководства пишет третий. Списком, а не одним `users.manage`
    // (`ADMIN_PAGE_PERMISSIONS`, `docs/manuals-plan.md` §3.6): держатель набора «Рассылки» маршрут
    // проходил, а пункта меню не видел — раздел открывался ему только по прямой ссылке.
    permissions: ADMIN_PAGE_PERMISSIONS,
    startOrder: 7,
  },
];

/**
 * Кабинет водителя (ADR 0102). В `SHELL_SECTIONS` его нет и быть не должно: у него свой каркас
 * (`DriverLayout`), своя ветка маршрутов и своя index-страница, а в меню портала он не показывается
 * вовсе. Общим с разделами каркаса у него ровно одно — условие входа, и потому строка в реестре у
 * него есть.
 *
 * `roles` — единственное место, где раздел спрашивает роль, и это не перестраховка: право
 * `driverCabinet.read` есть у администратора (у него есть все права словаря), и по одному праву
 * кабинет открывался ему и стартовой страницей, и по прямой ссылке — а выйти из второго контура
 * нечем: ни меню, ни разделов. Раздел, из которого нельзя выйти, для держателя всех прав означал
 * потерю портала целиком.
 */
export const DRIVER_CABINET_SECTION: DriverCabinetSection = {
  id: 'driver-cabinet',
  shell: 'own',
  path: '/driver',
  // Подписи у кабинета есть, хотя пункта меню нет: реестр общий, и раздел без имени нечем назвать
  // ни в тесте, ни в сообщении об отказе.
  label: 'Кабинет водителя',
  short: 'Кабинет',
  permissions: ['driverCabinet.read'],
  roles: PERSON_SCOPED_ROLES,
  startOrder: 1,
};

/** Все разделы портала: кабинет плюс каркас. Порядок массива значения не имеет — его задаёт `startOrder`. */
export const PORTAL_SECTIONS: readonly PortalSection[] = [
  DRIVER_CABINET_SECTION,
  ...SHELL_SECTIONS,
];

/**
 * Разделы в стартовом порядке — **посимвольная копия сегодняшнего перебора `homePath`** (кабинет,
 * вывоз, заказ ТС, гараж, листы, справочники, администрирование), к которой в хвост дописан
 * пропущенный раздел «Орг.техника».
 *
 * Хвост здесь — решение, а не остаток. Поставь «Орг.техника» на её место в меню (пятой), и механик
 * с назначенным `officeEquipment.read` (ADR 0106) уехал бы из «Гаража» в оргтехнику: приоритет
 * стартового раздела — продуктовый вопрос, и менять его побочным эффектом рефакторинга нельзя.
 * Именно этот порядок и есть гарантия совместимости: перебор профилей матрицы её не даёт — со
 * свободной сборкой полномочий набор прав учётки из роли больше не выводится.
 *
 * Считается один раз: список неизменен, а `startSection` зовут на каждом переходе портала.
 */
const SECTIONS_BY_START: readonly PortalSection[] = [...PORTAL_SECTIONS].sort(
  (a, b) => a.startOrder - b.startOrder,
);

/**
 * Чем портал отвечает на «открыт ли раздел». `canUse` передаётся предикатом, а не пересчитывается
 * реестром: права считает сервер (ADR 0106), контрактам их выводить нельзя, а область к `canUse`
 * уже приложена (ADR 0062).
 */
export interface SectionAccess {
  role: Role | null;
  canUse: (permission: Permission) => boolean;
}

/**
 * Открыт ли раздел: любое из его прав вместе с ролью, если раздел её требует.
 *
 * Один предикат на меню, гейты маршрутов и стартовую страницу. До него те же три вопроса задавались
 * тремя способами (`can` где-то, `canUse` где-то, роль плюс право у кабинета) — и живучесть бага
 * была следствием именно этого. Всюду `canUse`: область сужает один модуль (вывоз мусора), для
 * остальных прав `canUse` совпадает с `can`, и три разных ответа были следствием копий, а не смысла.
 */
export function isSectionOpen(section: PortalSection, access: SectionAccess): boolean {
  if (section.roles && (access.role === null || !section.roles.includes(access.role))) return false;
  return section.permissions.some((permission) => access.canUse(permission));
}

/** Пункты меню: только разделы каркаса и в порядке `SHELL_SECTIONS`. */
export function openShellSections(access: SectionAccess): readonly PortalShellSection[] {
  return SHELL_SECTIONS.filter((section) => isSectionOpen(section, access));
}

/**
 * Стартовый раздел учётки — первый открытый по `startOrder`. Кабинет водителя участвует наравне с
 * прочими: у роли `driver` нет ни одного права основного портала, и не будь его в переборе, водитель
 * приземлялся бы на смену пароля — единственное, что осталось бы ему доступным.
 *
 * `null` — это «разделов нет», и он **значение, а не адрес**: подставить сюда служебный маршрут
 * больше негде, а показать это состояние экраном — дело портала (§4.5 плана). Ровно этим отличается
 * «доступ не настроен» от «пароль просрочен», которые до реестра были для человека неразличимы.
 */
export function startSection(access: SectionAccess): PortalSection | null {
  return SECTIONS_BY_START.find((section) => isSectionOpen(section, access)) ?? null;
}
